/**
 * 车辆诊断设备版本变更表格信息(昆仑山设备)
 */
import React, { useState, useEffect, Fragment } from 'react';
import CommonTable from '@/components/commonTable';
import ExportButton from '@/components/exportButton';
import RenderEllipsisColumn from '@/components/renderEllipsisColumn';
import {
  carDiagnosisVersionRequest,
  carDiagnosisVersionRequestPath
} from '@/service/carDiagnosis';
import { useTable } from '@/utils/useTable';
import { formatTime } from '@/utils/formatTime';
import { renderDifferentTableCell } from '@/utils/common';

interface KlsVersionTableProps {
  diagnosisSearchParams: any;
  getExportParams: () => any;
}

const KlsVersionTable = (props: KlsVersionTableProps) => {
  const { diagnosisSearchParams, getExportParams } = props;
  const [searchParams, setSearchParams] = useState(diagnosisSearchParams);
  // 监听搜索参数的变化重新获取数据
  useEffect(() => {
    setSearchParams(diagnosisSearchParams);
  }, [diagnosisSearchParams]);

  const { page, limit, loading, data, totalCount, onPageChange } = useTable({
    requestFunc: carDiagnosisVersionRequest,
    initRequest: Boolean(diagnosisSearchParams),
    initPageSize: 40,
    searchParams
  });

  // 格式化无效版本号的展示形式
  const formatValue = (value: any) =>
    /fail to get/i.test(value) ? '无效版本号' : value;

  // 渲染表格列内容
  const renderColumnText = (value: any) => {
    return <RenderEllipsisColumn text={formatValue(value)} ellipsis />;
  };

  const _columns = [
    {
      title: 'Uboot版本号',
      dataIndex: 'ubootVersion',
      width: 150,
    },
    {
      title: 'Kernel版本号',
      dataIndex: 'kernelVersion',
      width: 150,
    },
    {
      title: '文件系统版本号',
      dataIndex: 'fileSystemVersion',
      width: 150,
    },
    {
      title: '控制器软件版本号',
      dataIndex: 'aebsSwVersion',
      width: 160,
    },
    {
      title: '控制器硬件版本号',
      dataIndex: 'aebsHwVersion',
      width: 160,
    },
    {
      title: '控制器SN号',
      dataIndex: 'aebsSn',
      width: 130,
    },
    {
      title: '控制器PN号',
      dataIndex: 'aebsPn',
      width: 130,
    },
    {
      title: '前向一体机软件本版号',
      dataIndex: 'aebCameraVersion',
      width: 180,
    },
    {
      title: '前向一体机 SN 号',
      dataIndex: 'aebCameraSn',
      width: 160,
    },
    {
      title: 'DMS相机软件版本号',
      dataIndex: 'dmsCameraVersion',
      width: 160,
    },
    {
      title: '记录仪版本号',
      dataIndex: 'carDvrVersion',
      width: 130,
    },
    {
      title: '左侧BSD相机软件版本号',
      dataIndex: 'bsdLeftCameraVersion',
      width: 180,
    },
    {
      title: '右侧BSD相机软件版本号',
      dataIndex: 'bsdRightCameraVersion',
      width: 180,
    },
    {
      title: '后向RVC相机软件版本号',
      dataIndex: 'rvcCameraVersion',
      width: 180,
    },
    {
      title: '前向毫米波雷达软件版本号',
      dataIndex: 'aebRadarVersion',
      width: 200,
    },
    {
      title: '左侧BSD角雷达软件版本号',
      dataIndex: 'bsdLeftRadarVersion',
      width: 190,
    },
    {
      title: '右侧BSD角雷达软件版本号',
      dataIndex: 'bsdRightRadarVersion',
      width: 190,
    },
    {
      title: '后向RVC毫米波雷达软件版本号',
      dataIndex: 'rvcRadarVersion',
      width: 210,
    },
    {
      title: '显示器软件版本号',
      dataIndex: 'displayerSwVersion',
      width: 160,
    },
    {
      title: '显示器SN号',
      dataIndex: 'displayerSn',
      width: 130,
    },
    {
      title: '4G设备软件版本号',
      dataIndex: 't4gVersion',
      width: 160,
    },
    {
      title: 'IMU软件版本号',
      dataIndex: 'imuVersion',
      width: 140,
    },
    {
      title: '执行机构软件版本号',
      dataIndex: 'cableMotorVersion',
      width: 170,
    },
    {
      title: '执行机构SN号',
      dataIndex: 'cableMotorSn',
      width: 140,
    },
    {
      title: 'SIM卡ICCID',
      dataIndex: 'iccid',
      width: 150,
    },
  ].map((item: any) => {
    const returnItem = { ...item };
    const { noCommonRender, noCommonCell, dataIndex } = item;
    if (!noCommonRender) {
      returnItem.render = (value: any) => renderColumnText(value);
    }
    if (!noCommonCell) {
      returnItem.onCell = (record: any, rowIndex: number) =>
        renderDifferentTableCell(
          record,
          rowIndex,
          dataIndex,
          data,
          formatValue
        );
    }
    return returnItem;
  });
  const columns = [{
    title: '上报时间',
    dataIndex: 'reportedTime',
    width: 180,
  }, ..._columns];

  return (
    <Fragment>
      <div style={{ margin: '12px 0', textAlign: 'right' }}>
        <ExportButton
          type="primary"
          disabled={!diagnosisSearchParams}
          url={carDiagnosisVersionRequestPath}
          fileName={`${diagnosisSearchParams?.plateNumber}设备版本变更`}
          getParamsFunc={getExportParams}
          requestMethod="GET"
          permissionKey="/car-admin/car-diagnosis/version-export"
        />
      </div>
      <CommonTable
        rowKey="id"
        columns={columns}
        dataSource={data}
        pagination={{
          total: totalCount,
          current: page,
          pageSize: limit,
          onChange: onPageChange
        }}
        bordered
        loading={loading}
        scroll={{ x: '100%', y: 400 }}
      />
    </Fragment>
  );
};

export default KlsVersionTable;
